import { Component, OnInit, Input, Output, EventEmitter, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { ImageDate } from '../models/imageDate';
import { MONTHS, WriteOrdinalSuffixOfNumber } from 'starbase-launcher';
import { range } from 'rxjs';
import { filterWindowSlideIn } from '../animations';
import { StarbaseNotificationService, StarbaseNotificationTypes } from 'starbase-notification';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { BreakpointService, BreakpointState } from '../breakpoint.service';

const focusedYears = [
  1860, 1861, 1862, 1863, 1864, 1865, 1866
]; // years of the US Civil War proper

@Component({
  selector: 'app-date-filter',
  templateUrl: './date-filter.component.html',
  styleUrls: ['./date-filter.component.css'],
  animations: [ filterWindowSlideIn ]
})
export class DateFilterComponent implements OnInit, AfterViewInit {

  @Input() datesData: ImageDate[];
  @Input() showQuarters = false;
  @Input() activeDateFilters: ImageDate[] = [];
  @Input() dropdownTypeDebounceTime = 900; // ms

  @Output() filtersSet: EventEmitter<object[]> = new EventEmitter<object[]>();
  @Output() closePressed: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('yearDropdown' , { static: false }) yearDropdown: MatSelect;
  @ViewChild('monthDropdown' , { static: false }) monthDropdown: MatSelect;
  @ViewChild('dateDropdown' , { static: false }) dateDropdown: MatSelect;
  @ViewChild('qtrDropdown') qtrDropdown: MatSelect;


  listOfYears: number[] = []; // list of non-war years
  listOfFocusedYears: number[] = []; // list of proper US Civil War years in dataset
  months = JSON.parse(JSON.stringify(MONTHS)); // shallow copy to ensure helper array is not mutated. Using 'selected' pseudo property to indicate availability on the dropdowns
  ordinalSuffix = WriteOrdinalSuffixOfNumber;
  dates: [ number, boolean ][] = [];

  chosenYear = null;
  chosenMonth = null;
  chosenQuarter = null;
  chosenDate = null;

  show: string;
  private _subscription;
  isHandset: boolean = false;

  constructor(private notiSvc: StarbaseNotificationService, private breakpointService: BreakpointService) {
    range(1, 31).subscribe(d => this.dates.push([d, false]));
    this.months.splice(0, 1); // remove 'Unknown,' which is handled by hardcoded option
   }

  ngOnInit() {

    this._subscription = this.breakpointService.handset$.subscribe(
      isHandset => {
        this.isHandset = isHandset;
      }
    );

    if (!this.datesData) {
      console.error('Date Filter component did not all receive necessary data.');
      return;
    }
    this.show = 'in'; // animate
    // onlyUniqueByKey(this.datesData, 'year').forEach(y => this.listOfYears.push(y.year));
    this.getUniqueYears(this.datesData);
    this.listOfYears = this.listOfYears.filter(a => a !== 0); // remove a '0' entry that would be handled by the 'Unknown' option.
    this.listOfFocusedYears = this.listOfYears.filter(a => focusedYears.includes(a)); // find which years of the war were returned by the server
    this.listOfYears = this.listOfYears.filter(a => !this.listOfFocusedYears.includes(a)); // remove those years from the whole list displayed
    this.listOfYears = this.listOfYears.sort((a, b) => a - b); // sort the remaining years chronologically.
    this.listOfFocusedYears = this.listOfFocusedYears.sort((a, b) => a - b); // chrono sort focused years
  }
  
  ngAfterViewInit() {
    this.reset();
    if (this.yearDropdown) { setTimeout(() => this.yearDropdown.focus(), 1); }
  }

  private getUniqueYears(data: ImageDate[]): void {
    [...new Set(data.map(i => i.year))].map(y => this.listOfYears.push(y));
  }

  changeYear(newYear: MatSelectChange | null): void {
    this.chosenYear = newYear ? newYear.value : null;
    this.changeMonth(null);
    this.changeDate(null);
    this.chosenQuarter = null;
    if (this.chosenYear === null) { // don't filter available months if user chose Any
      this.months.forEach(m => m.selected = true);
      return;
    }
    this.months.forEach(m => {
      m['selected'] = false;
      this.datesData.filter(a => a.year === this.chosenYear).forEach(ym => {
        if (ym.month === m.val) {
          m['selected'] = true;
        }
      });
    });
  }

  changeMonth(newMonth: MatSelectChange | null): void {
    this.chosenMonth = newMonth ? newMonth.value : null;
    this.changeDate(null);
    // if (this.chosenQuarter !== 0 && this.chosenQuarter !== null) {
    //   this.chosenQuarter = ImageDate.findQtrValue(this.chosenMonth);
    // }
    if (this.chosenMonth === null) { // don't filter available dates if user chose Any
      this.dates.forEach(d => d[1] = true);
      return;
    }
    this.dates.forEach(d => {
      d[1] = false;
      this.datesData.filter(a => (!this.chosenYear || a.year === this.chosenYear) && a.month === this.chosenMonth).forEach(yd => {
        if (yd.day === d[0]) {
          d[1] = true; // selected marked
        }
      });
    });
  }
  changeDate(newDate: MatSelectChange | null): void {
    this.chosenDate = newDate ? newDate.value : null;

  }
  changeQtr(newQtr: MatSelectChange | null): void {
    this.chosenQuarter = newQtr ? newQtr.value : null;
  }

  public reset(): void {
    this.chosenYear = null;
    this.chosenMonth = null;
    this.chosenDate = null;
    this.chosenQuarter = null;
    this.yearDropdown.writeValue(null);
    this.monthDropdown.writeValue(null);
    this.dateDropdown.writeValue(null);
    if (this.showQuarters) { this.qtrDropdown.writeValue(null); }
    // reset selected flags:
    this.dates.forEach(d => d[1] = true);
    this.months.forEach(m => m['selected'] = true);
    console.log('Date filter picker choices reset.');
    this.yearDropdown.focus();
  }

  addToFilterList() {
    if (!this.chosenYear
      && !this.chosenMonth
      && !this.chosenDate
      && !this.chosenQuarter) {
      this.notiSvc.postNotification(this.notiSvc.notificationBuilder(
        'Invalid Date Filter Specified', 'Please specify at least one part of a date.', StarbaseNotificationTypes.Warning, 3000));
      return false;
    }
    const theDate = new ImageDate(this.chosenMonth);
    theDate.year = this.chosenYear;
    theDate.month = this.chosenMonth;
    theDate.day = this.chosenDate;
    theDate.quarter = this.chosenQuarter;
    this.activeDateFilters.push(theDate);
    this.filtersSet.emit(this.activeDateFilters);
    this.reset();
  }
  public removeFromFilterList(index: number) {
    this.activeDateFilters.splice(index, 1);
    this.filtersSet.emit(this.activeDateFilters);
  }

  public closeFilterBox(clear: boolean): void {
    if (clear) {
      this.activeDateFilters.splice(0, this.activeDateFilters.length);
      this.filtersSet.emit(this.activeDateFilters);
    }
    this.show = 'out';
    setTimeout(() =>
      this.closePressed.emit(), 300);
  }

}
